
import { Component, Mixins, Watch } from "vue-property-decorator";
import ResearchMixin from "@/views/research/research.mixin";
import BaseMixin from "@/views/base/base.mixin";
import { SidebarType } from "@/views/sidebar/sidebar.types";
import { ResearchParticipant, SETTINGS, DISPLAY } from "@/views/research/research.types";
import moment from "moment";
import VueSelect from 'vue-select';

type VueSelectInstance = InstanceType<typeof VueSelect>;

@Component
export default class ResearchComponent extends Mixins(ResearchMixin, BaseMixin) {
  alertVariant: "success" | "danger" = "success";
  settings = SETTINGS.NONE;
  showHide = DISPLAY.SHOW;
  alertVisible = false;
  disableCheckboxes = false;
  loading = false;
  message = "";

  get SETTINGS() {
    return SETTINGS;
  }

  get tableFields() {
    let fields = [ ...this.fields ]
    if (this.settings === SETTINGS.NOTIFICATION) {
      fields.unshift({ key: 'select', label: 'Select' })
      fields = fields.filter(f => f.key !== 'action');
    } else if (this.settings === SETTINGS.SHOW_HIDE) {
      fields.unshift({ key: 'show', label: 'show' })
      fields = fields.filter(f => f.key !== 'action');
    }
    return fields;
  }

  get sortedAndFilteredItems() {
    const query = this.searchQuery.trim().toLowerCase();
    const field = this.sortBy;
    const direction = this.sortDesc ? -1 : 1;

    let items = [...this.researchParticipants];

    if (query) {
      items = items.filter(item => {
        return (
          item.patientId?.toLowerCase().includes(query) ||
          item.watchId?.toLowerCase().includes(query) ||
          item.medication?.toLowerCase().includes(query) // may add more later
        );
      });
    }

    items.sort((a, b) => {
      const valA = (a as any)[field];
      const valB = (b as any)[field];

      const isNullA = valA === null || valA === undefined || valA === '';
      const isNullB = valB === null || valB === undefined || valB === '';

      if (isNullA && !isNullB) return 1;
      if (!isNullA && isNullB) return -1;
      if (isNullA && isNullB) return 0;

      if (typeof valA === 'number' && typeof valB === 'number') {
        return direction * (valA - valB);
      }

      if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        return direction * (Number(valA) - Number(valB));
      }

      return direction * String(valA).localeCompare(String(valB));
    });

    return items;
  }
  
  sort(field: string) {
    if (this.sortBy === field) {
      this.sortDesc = !this.sortDesc;
    } else {
      this.sortBy = field;
      this.sortDesc = false;
    }
  }

  getSortIconClass(field: string, descending: boolean) {
    return {
      'sort-icon-active': this.sortBy === field && this.sortDesc === descending,
      'sort-icon-inactive': !(this.sortBy === field && this.sortDesc === descending)
    };
  }

  async mounted() {
    this.loading = true;
    const client = await this.waitForClientReady();
    if (client) {
      await this.fetchData();
    } else {
      console.warn("Client not ready in time.");
    }
    this.loading = false;
  }

  async fetchData() {
    await this.$store.dispatch("Research/fetchResearchParticipants", {
      clientId: this.selectedClientDDL.value,
      timezone: this.selectedClient.timezone,
      show: this.showHide === DISPLAY.SHOW ? true : false
    });
  }

  calculateEnrollmentDays(researchDay1: string): number {
    const reseachDay = moment(researchDay1, 'YYYY-MM-DD').startOf('day');
    const today = moment().startOf('day');

    const diff = Math.max(1, today.diff(reseachDay, 'days') + 1);
    return Math.min(diff, this.selectedResearchDuration);
  }

  addParticipant() {
    this.$sidebar.open({
      type: SidebarType.Participant,
      title: "Add Participant",
      useHistory: true,
      item: new ResearchParticipant(),
      subItem: { isEdit: false }
    });
  }

  async showOrHide(selectedResearchParticipant: ResearchParticipant) {
    this.loading = true;
    this.settings = SETTINGS.SHOW_HIDE;
    await this.$store.dispatch("Summary/updateParticipantShow", {
      clientId: this.selectedClientDDL.value,
      participantId: selectedResearchParticipant.participantId,
      show: !selectedResearchParticipant.show,
    });
    if (this.errorMessage) {
      this.message = this.errorMessage;
      this.alertVariant = "danger";
      this.alertVisible = true;
      this.loading = false;
      setTimeout(() => {
        this.alertVisible = false;
      }, 5000);
    } else {
      await this.fetchData();
      this.loading = false;
    }
  }

  editParticipant(selectedResearchParticipant: ResearchParticipant) {
    this.$sidebar.open({
      type: SidebarType.Participant,
      title: "Edit Participant",
      useHistory: true,
      item: selectedResearchParticipant,
      subItem: { isEdit: true }
    });
  }

  showResearchData(selectedResearchParticipant: ResearchParticipant) {
    this.$sidebar.open({
      type: SidebarType.ResearchData,
      title: "Engagement & Compliance",
      useHistory: true,
      item: selectedResearchParticipant
    });
  }

  onRadioChange(selectedResearchParticipant: ResearchParticipant) {
    this.selectedResearchParticipant = selectedResearchParticipant;
    this.disableCheckboxes = true;
    this.$sidebar.open({
      type: SidebarType.SendNotification,
      title: "Send Text, Survey, or Assessment",
      useHistory: true,
      item: {
        phoneNumber: this.selectedResearchParticipant.phoneNumber,
        participantId: this.selectedResearchParticipant.participantId,
        patientId: this.selectedResearchParticipant.patientId
      },
    });
  }

  async showShowHide() {
    this.loading = true;
    this.settings = SETTINGS.SHOW_HIDE;
    await this.fetchData();
    this.loading = false;
  }

  async showNotification() {
    this.loading = true;
    this.settings = SETTINGS.NOTIFICATION;
    await this.fetchData();
    this.loading = false;
  }

  async onCancel() {
    this.loading = true;
    this.settings = SETTINGS.NONE;
    this.showHide = DISPLAY.SHOW;
    await this.fetchData();
    this.loading = false;
  }

  handleBlur(refName: string): void {
    const selectComponent = this.$refs[refName] as VueSelectInstance
    const input = selectComponent?.$el?.querySelector('input')
    input?.blur()
  }

  @Watch("selectedClientDDL")
  async onClientChange() {
    this.loading = true;
    this.settings = SETTINGS.NONE;
    await this.fetchData();
    this.loading = false;
  }

  @Watch('showHide')
  async onShowHideChange() {
    this.loading = true;
    await this.fetchData();
    this.loading = false;
  }

  @Watch("$sidebar.options.reload")
  async onReload(newVal: boolean) {
    if (newVal) {
      this.loading = true;
      await this.fetchData();
      this.loading = false;
    }
  }

  @Watch('$sidebar.isOpen')
  onSidebarOpened(isOpen: boolean) {
    if (!isOpen) {
      this.settings = SETTINGS.NONE;
      this.selectedResearchParticipant = new ResearchParticipant();
    }
  }
}
